import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import LoadingButton from "_shared/_views/loading/loading-button";
import PlaceAutocomplete from "_shared/addresses/_views/place-autocomplete";
import Address from "_shared/addresses/address";
import EmailAddressInput from "_shared/email-addresses/_views/email-address-input";
import EmailAddress from "_shared/email-addresses/email-address";
import NameInput from "_shared/names/_views/name-input";
import Name from "_shared/names/name";
import TitleInput from "_shared/titles/_views/inputs/title-input";
import Title from "_shared/titles/title";
import EntityNameInput from "legal-entities/entity-names/_views/inputs/entity-name-input";
import EntityName from "legal-entities/entity-names/entity-name";
import InvitationMessageInput from "legal-entities/invitations/messages/_views/inputs/invitation-message-input";
import InvitationMessage from "legal-entities/invitations/messages/invitation-message";
import EntityOfficerInvitationAPIService from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import { EntityOfficerInvitationParams } from "legal-entities/invitations/officer-invitations/_schema/entity-officer-invitation-schema";
import EntityOfficerInvitation, {
  InvalidEntityOfficerInvitationError,
} from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { RegistrationInfoParams } from "legal-entities/registration-info/_schema/registration-info-schema";
import RegistrationInfo, { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import { enqueueSnackbar } from "notistack";
import { useMemo, useRef, useState } from "react";
import { useSession } from "users/__session/session-context";

const OfficerInvitationFormContainer = styled("div")(({ theme }) => ({
  padding: theme.spacing(1),
  position: "relative",
  width: "100%",
}));
const Fieldset = styled("fieldset")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  minWidth: 0,
  flex: "1 1 300px",
  "&:last-of-type": {
    marginBottom: theme.spacing(2),
  },
}));
const OfficerInvitationFields = styled(Box)(({ theme }) => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));
const InvitationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

type InvitationFormErrors = {
  general?: string;
  title?: string;
  name?: string;
  email?: string;
  entityName?: string;
  location?: string;
  message?: string;
};

type EntityOfficerInvitationFormProps = {
  className?: string;
  onInvitationCreated: (invitation: EntityOfficerInvitation) => void;
};

export default function EntityOfficerInvitationForm(props: Readonly<EntityOfficerInvitationFormProps>) {
  const { className, onInvitationCreated } = props;

  const locationServicesRef = useRef<boolean>(false);

  const [inviteeEmail, setInviteeEmail] = useState<EmailAddress>();
  const [inviteeEntityName, setInviteeEntityName] = useState<EntityName>();
  const [inviteeTitle, setInviteeTitle] = useState<Title>();
  const [inviteeAddress, setInviteeAddress] = useState<Address>();
  const [inviteeName, setInviteeName] = useState<Name>();
  const [invitationMessage, setInvitationMessage] = useState<InvitationMessage>();
  const [messageDebouncing, setMessageDebouncing] = useState<boolean>(false);
  
  const [key, setKey] = useState<number>(0);

  const [sending, setSending] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<InvitationFormErrors>({});

  const session = useSession();

  function updateFormError(type: keyof InvitationFormErrors, error?: string) {
    if (!error) return;
    let errors: InvitationFormErrors = { ...formErrors };
    errors[type] = error;
    setFormErrors(errors);
  }

  const invitationParams = useMemo((): Partial<
    Omit<RegistrationInfoParams & EntityOfficerInvitationParams, "registrationInfo">
  > => {
    return {
      email: inviteeEmail,
      title: inviteeTitle,
      name: inviteeName,
      entityName: inviteeEntityName,
      location: inviteeAddress,
      message: invitationMessage,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inviteeEmail,
    inviteeTitle,
    inviteeName?.firstName,
    inviteeName?.lastName,
    inviteeEntityName,
    inviteeAddress?.address1,
    inviteeAddress?.address2,
    inviteeAddress?.city,
    inviteeAddress?.country,
    inviteeAddress?.coordinates,
    inviteeAddress?.postalCode,
    inviteeAddress?.state,
    invitationMessage,
  ]);

  const formIsValid = useMemo((): boolean => {
    try {
      validateFormFields(invitationParams);
      return true;
    } catch {
      return false;
    }
  }, [invitationParams]);

  function buildInvitation(
    params: Partial<Omit<RegistrationInfoParams & EntityOfficerInvitationParams, "registrationInfo">>,
  ): EntityOfficerInvitation | undefined {
    try {
      validateFormFields(params);
      return EntityOfficerInvitation.create({
        params: {
          ...params,
          registrationInfo: RegistrationInfo.create({
            params,
            options: { silenceSchemaErrors: true },
          }),
        },
        options: { silenceSchemaErrors: true },
      });
    } catch (error) {
      if (error instanceof InvalidEntityOfficerInvitationError || error instanceof InvalidRegistrationInfoError) {
        setFormErrors({ general: error.message });
        return;
      }
      throw error;
    }
  }

  async function sendInvitation() {
    setSending(true);

    try {
      const builtInvitation = buildInvitation(invitationParams);
      if (!builtInvitation) throw new InvalidEntityOfficerInvitationError("Invitation is invalid");
      const service = new EntityOfficerInvitationAPIService(session);
      const updatedInvitation = await service.inviteEntityOfficer(builtInvitation);
      enqueueSnackbar("Sent entity officer invitation", { variant: "success" });
      onInvitationCreated(updatedInvitation);
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar("Failed to send entity officer invitation", {
        variant: "error",
      });
    } finally {
      clearForm();
      setSending(false);
    }
  }

  function clearForm() {
    setInviteeEmail(undefined);
    setInviteeAddress(undefined);
    setInviteeTitle(undefined);
    setInviteeEntityName(undefined);
    setInviteeName(undefined);
    setInvitationMessage(undefined);
    setKey(i => i + 1);
  }

  return (
    <OfficerInvitationFormContainer className={className}>
      <InvitationFormTitle variant="h6">Invite Entity Officer</InvitationFormTitle>
      <OfficerInvitationFields>
        <Fieldset>
          <legend>
            <Typography variant="subtitle2">Officer Details</Typography>
          </legend>
          <TitleInput
            key={`title-${key}`}
            idPrefix="officer-invitation"
            fullWidth
            initialValue={inviteeTitle ?? undefined}
            onChange={(updatedTitle?: Title | undefined, error?: string) => {
              setInviteeTitle(updatedTitle);
              updateFormError("title", error);

              if (updatedTitle && !error) {
                buildInvitation({
                  ...invitationParams,
                  title: updatedTitle,
                });
              }
            }}
          />
          <NameInput
            key={`name-${key}`}
            idPrefix="officer-invitation"
            fullWidth
            initialValue={inviteeName}
            onChange={(updatedName?: Name | undefined, errorMessage?: string) => {
              setInviteeName(updatedName);
              updateFormError("name", errorMessage);

              if (updatedName && !errorMessage) {
                buildInvitation({
                  ...invitationParams,
                  name: updatedName,
                });
              }
            }}
            direction="column"
          />
          <EmailAddressInput
            key={`email-${key}`}
            idPrefix="officer-invitation"
            fullWidth
            initialValue={inviteeEmail}
            onChange={(updatedEmail?: EmailAddress, error?: string) => {
              if (updatedEmail) {
                setInviteeEmail(updatedEmail);
                buildInvitation({
                  ...invitationParams,
                  email: updatedEmail,
                });
              }
              updateFormError("email", error);
            }}
          />
        </Fieldset>
        <Fieldset>
          <legend>
            <Typography variant="subtitle2">Entity Details</Typography>
          </legend>
          <EntityNameInput
            key={`entityName-${key}`}
            idPrefix="officer-invitation"
            fullWidth
            label="Name"
            initialValue={inviteeEntityName}
            onChange={(updatedEntityName?: EntityName | undefined, error?: string) => {
              setInviteeEntityName(updatedEntityName);
              updateFormError("entityName", error);

              if (updatedEntityName && !error) {
                buildInvitation({
                  ...invitationParams,
                  entityName: updatedEntityName,
                });
              }
            }}
          />
          <PlaceAutocomplete
            key={`location-${key}`}
            label="Location"
            variant="outlined"
            required
            value={inviteeAddress ?? null}
            locationServicesDisabledRef={locationServicesRef}
            onLocationServicesDisabledChange={(newValue) => {
              locationServicesRef.current = newValue;
            }}
            onChange={(address: Address | null, error?: string) => {
              setInviteeAddress(address ?? undefined);
              updateFormError("location", error);
              
              if(address && !error)
              {
                buildInvitation({
                  ...invitationParams,
                  location: address
                })
              }
            }}
          />
        </Fieldset>
        <InvitationMessageInput
          key={`message-${key}`}
          idPrefix="officer-invitation"
          initialValue={invitationMessage}
          onChange={(updatedMessage?: InvitationMessage, error?: string) => {
            setInvitationMessage(updatedMessage);
            updateFormError("message", error);

            if (updatedMessage && !error) {
              buildInvitation({
                ...invitationParams,
                message: updatedMessage,
              });
            }
          }}
          onDebounceStart={() => { setMessageDebouncing(true) }}
          onDebounceEnd={() => { setMessageDebouncing(false) }}
        />
      </OfficerInvitationFields>
      <LoadingButton
        id="officer-invitation-submit-button"
        color="primary"
        disabled={sending || !formIsValid || messageDebouncing}
        loading={sending}
        onClick={sendInvitation}
        startIcon={<SendIcon />}
        variant="contained"
      >
        Send Invite
      </LoadingButton>
    </OfficerInvitationFormContainer>
  );
}

function validateFormFields(
  formFields: Partial<Omit<RegistrationInfoParams & EntityOfficerInvitationParams, "registrationInfo">>,
): asserts formFields is Omit<RegistrationInfoParams & EntityOfficerInvitationParams, "registrationInfo"> {
  const missingFields: string[] = [];

  if (!formFields.title) missingFields.push("Title");
  else if (!(formFields.title instanceof Title)) throw new Error("Title is invalid type");

  if (!formFields.name) missingFields.push("Name");
  else if (!(formFields.name instanceof Name)) throw new Error("Name is invalid type");

  if (!formFields.email) missingFields.push("Email Address");
  else if (!(formFields.email instanceof EmailAddress)) throw new Error("Email Address is invalid type");

  if (!formFields.entityName) missingFields.push("Entity Name");
  else if (!(formFields.entityName instanceof EntityName)) throw new Error("Entity Name is invalid type");

  if (!formFields.location) missingFields.push("Entity Location");
  else if (!(formFields.location instanceof Address)) throw new Error("Entity Location is invalid type");

  if (missingFields.length > 0) {
    throw new InvalidEntityOfficerInvitationError(`Missing required fields: ${missingFields.join(", ")}`);
  }
}
